/*
 * Copyright (c) 2023 Trimble Inc. All rights reserved.
 */

import { Snackbar, SnackbarContent, Typography } from '@mui/material';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import ClickAwayListener from '@mui/material/ClickAwayListener';
import Grid from '@mui/material/Grid';
import Grow from '@mui/material/Grow';
import IconButton from '@mui/material/IconButton';
import Input from '@mui/material/Input';
import MenuItem from '@mui/material/MenuItem';
import MenuList from '@mui/material/MenuList';
import Paper from '@mui/material/Paper';
import Popper from '@mui/material/Popper';
import { withStyles } from '@mui/styles';
import Tooltip from '@mui/material/Tooltip';
import DeleteIcon from '@mui/icons-material/Delete';
import SearchIcon from '@mui/icons-material/Search';
import moment from 'moment';
import * as React from 'react';
import './SearchSection.css';


export type searchFilterOptions = {
    Icon: React.ElementType;
    suffix: string;
    searchProp: string;
};

const styles = (theme: any) => ({
    chip: {
        margin: (theme && typeof theme.spacing === 'function') ? theme.spacing(0.5) : '4px'
    }
});

export type filterPropOptions = {
    Icon: React.ElementType;
    filterProp: string;
    filterText: string;
};

export type searchSectionProps = {
    classes: any;
    endDate?: moment.Moment | null;
    onSearch: (filters: any) => void;
    searchSuggestions?: searchFilterOptions[];
    startDate?: moment.Moment | null;
    helperTextProps?: string;
};

export type searchSectionViewState = {
    endDate: moment.Moment | null;
    filters: any;
    searchSuggestionOpen: boolean;
    searchText: string;
    startDate: moment.Moment | null;
    cursor: any;
    validationError: string;
};

const DatePickerRenderer: React.FunctionComponent<{ date: moment.Moment; onChange: any; }> = props => {
    return (<div className="date-picker-placeholder" />);
};

export class SearchSection extends React.Component<
    searchSectionProps,
    searchSectionViewState
> {
    private searchElement: any;
    constructor(props: searchSectionProps) {
        super(props);
        this.state = {
            endDate:
                props.endDate && moment(props.endDate).isValid() ? props.endDate : null,
            filters: [],
            searchSuggestionOpen: false,
            searchText: '',
            startDate:
                props.startDate && moment(props.startDate).isValid()
                    ? props.startDate
                    : null,
            cursor: -1,
            validationError: ''
        };
    }

    public onSearchClick = () => {
        if (navigator.onLine) {
            const filters = {
                searchText: this.state.searchText,
                ...(this.props.searchSuggestions && {
                    suggestionsFilter: this.state.filters
                }),
                ...(this.props.startDate && {
                    startDate: this.state.startDate
                }),
                ...(this.props.endDate && {
                    endDate: this.state.endDate
                })
            };

            return (
                typeof this.props.onSearch === 'function' && this.props.onSearch(filters)
            );
        }
    };

    public searchByDate = () => {
        this.setState({ filters: [] });
        const filters = {
            searchText: this.state.searchText,
            ...(this.props.startDate && {
                startDate: this.state.startDate
            }),
            ...(this.props.endDate && {
                endDate: this.state.endDate
            })
        };
        return (
            typeof this.props.onSearch === 'function' && this.props.onSearch(filters)
        );
    };
    public onSearchTextChange = (event: any) => {
        this.setState({
            searchText: event.target.value,
            searchSuggestionOpen: true,
            validationError: ''
        });
    };
    public closeSearchSuggestion = () => {
        this.setState({ searchSuggestionOpen: false });
    };
    public openSearchSuggestion = () => {
        this.setState({ searchSuggestionOpen: true });
    };
    public handleOnClickAway = (event: any) => {
        if (this.searchElement.contains(event.target)) {
            return;
        }
        this.closeSearchSuggestion();
    };
    public handleSearchSuggestionClick = (suggestion: any) => (event: any) => {
        this.validate(suggestion);
    };
    validate(suggestion: any): any {
        if (!this.isDuplicateFilter(suggestion.searchProp)) {
            //validations
            let validationError = '';
            switch (suggestion.searchProp) {
                case 'id':
                case 'age':
                    validationError = /^\d+$/.test(this.state.searchText) === false ? 'Value must be a number' : '';
                    break;
                case 'serialnumber':
                    validationError = /^[a-zA-Z0-9]+$/.test(this.state.searchText) === false ? 'Please enter a valid serial number' : '';
                    break;
                case 'partnumber':
                    validationError = /^[a-zA-Z0-9\-]+$/.test(this.state.searchText) === false ? 'Please enter a valid part number' : '';
                    break;
                case 'username':
                case 'licensetype':
                case 'usertype':
                    validationError = /^[a-zA-Z\s]+$/.test(this.state.searchText) === false ? `Value must contain only alphabets` : '';
                    break;
                case 'opendate':
                case 'closeddate':
                    validationError = isNaN(Date.parse(this.state.searchText)) === true ? 'Please enter a valid date' : '';
                    break;
                case 'repairtime':
                    let strhour = null, strminute = null;
                    const splitarray = this.state.searchText.split('h');
                    if (splitarray.length > 0) { strhour = splitarray[0] == null ? null : splitarray[0]; }
                    if (splitarray.length > 1) { strminute = splitarray[1] == null ? null : splitarray[0]; }
                    const ishour = strhour != null ? /^\d+$/.test(strhour) : false;
                    const isminute = strminute != null ? /^\d+$/.test(strminute) : false;
                    if (ishour === false && isminute === false) {
                        validationError = 'Repair time is not in valid format.';
                    }
                    break;
                case 'machinename':
                    validationError = /^[a-zA-Z0-9-\s]+$/.test(this.state.searchText) === false ? 'Please enter a valid machine name' : '';
                    break;
            }
            if (validationError !== '') {
                this.setState({ validationError });
            }
            else {
                this.setState(
                    {
                        filters: [
                            ...this.state.filters,
                            {
                                Icon: suggestion.Icon,
                                toolTip: suggestion.toolTip,
                                filterProp: suggestion.searchProp,
                                filterText: this.state.searchText
                            }
                        ],
                        searchText: '',
                        cursor: 0
                    },
                    () => this.onSearchClick()
                );
            }
        }
        this.closeSearchSuggestion();
    };
    public onStartDateChange = (date: moment.Moment) => {
        this.setState({ startDate: date });
    };
    public onEndDateChange = (date: moment.Moment) => {
        this.setState({ endDate: date });
    };
    public handleRemoveFilter = (data: any) => () => {
        if (navigator.onLine) {
            this.setState(
                state => {
                    const filters = [...state.filters];
                    const chipToDelete = filters.indexOf(data);
                    filters.splice(chipToDelete, 1);
                    return { filters };
                },
                () => this.onSearchClick()
            );
        }
    };
    public handleClearAllFilter() {
        this.setState(
            state => {
                let filters = [...state.filters];
                filters = [];
                return { filters };
            },
            () => this.onSearchClick()
        );
    }

    // Check if the given search prop and search text forms a duplicate filter
    public isDuplicateFilter = (searchProp: any, searchText?: string) => {
        return !(this.state.filters.findIndex(
            (f: filterPropOptions) =>
                searchProp === f.filterProp &&
                (searchText || this.state.searchText).toLowerCase() ===
                f.filterText.toLowerCase()
        ) < 0);
    };

    public setDatePicker = (date: moment.Moment, isStart: boolean) => {
        return (
            <DatePickerRenderer
                date={date}
                onChange={isStart ? this.onStartDateChange : this.onEndDateChange}
            />
        );
    };

    public addFilter = (filter: filterPropOptions) => {
        if (
            filter &&
            !this.isDuplicateFilter(filter.filterProp, filter.filterText)
        ) {
            const filters = [...this.state.filters];
            filters.push(filter);
            this.setState({ filters }, () => this.onSearchClick());
        }
    };

    public searchButton = () => (
        <div className="search-button-wrapper">
            <Button variant="contained" color="secondary" className="search-button" onClick={this.searchByDate}>
                <Typography color="primary">Search</Typography>
            </Button>
        </div>
    );

    handleKeyDown(e: any) {
        const { cursor } = this.state;
        const { searchSuggestions } = this.props;

        if (searchSuggestions !== undefined) {
            // arrow up/down button should select next/previous list element
            if (e.keyCode === 38 && cursor > 0) {
                this.setState(prevState => ({
                    cursor: prevState.cursor - 1
                }));
            } else if (e.keyCode === 40 && cursor < searchSuggestions.length - 1) {
                this.setState(prevState => ({
                    cursor: prevState.cursor + 1
                }));
            } else if (e.keyCode === 38 && cursor === 0) {
                this.setState(prevState => ({
                    cursor: searchSuggestions.length - 1
                }));
            } else if (e.keyCode === 40 && cursor === searchSuggestions.length - 1) {
                this.setState(prevState => ({
                    cursor: 0
                }));
            }
            else if (e.keyCode === 13 && cursor !== -1) {
                const suggestion = searchSuggestions[cursor];
                this.validate(suggestion);
            }
        }
    }

    public handleEnterKeyDown = (callback: Function) => (event: any) => {
        if (event.keyCode === 13) {
            callback();
        }
    };
    handleClose = (event: any, reason: any) => {
        if (reason === 'clickaway') {
            return;
        }
        this.setState({ validationError: '' });
    };
    public render() {
        const {
            searchText,
            searchSuggestionOpen,
            filters,
        } = this.state;
        const { searchSuggestions, helperTextProps } = this.props;
        return (
            <div className="filter-search-container">
                <Grid container className="filter-search">
                    <div className="search-input-container">
                        <Input
                            inputRef={this.setSearchElement}
                            className="search-input"
                            placeholder="Search"
                            value={searchText}
                            onChange={this.onSearchTextChange}
                            onFocus={this.openSearchSuggestion}
                            startAdornment={<SearchIcon />}
                            onKeyDown={this.handleKeyDown.bind(this)}
                            tabIndex={0}
                            disabled={!navigator.onLine}
                        />
                        {filters.length > 0 && (
                            <Tooltip title="Clear all filters">
                                <IconButton
                                    color="primary"
                                    aria-label="Delete"
                                    disabled={!navigator.onLine}
                                    onClick={this.handleClearAllFilter.bind(this)}
                                >
                                    <DeleteIcon />
                                </IconButton>
                            </Tooltip>
                        )}
                        <Snackbar
                            anchorOrigin={{
                                horizontal: 'left',
                                vertical: 'bottom'
                            }}
                            open={this.state.validationError !== ''}
                            autoHideDuration={5000}
                            onClose={this.handleClose}
                        >
                            <SnackbarContent
                                className="search-input-snackbar"
                                message={this.state.validationError}
                            />
                        </Snackbar>


                        <Popper className="search-suggestion-popper" open={searchSuggestionOpen && searchText.trim() !== ''} anchorEl={this.searchElement} placement="bottom-start">
                            {searchSuggestions && (
                                <Grow in={searchSuggestionOpen && searchText.trim() !== ''} className="search-popup">
                                    <Paper>
                                        <ClickAwayListener mouseEvent={'onClick'} onClickAway={this.handleOnClickAway}>
                                            <MenuList>
                                                {searchText && searchSuggestions && searchSuggestions.map(this.searchSuggestionsRenderer)}
                                            </MenuList>
                                        </ClickAwayListener>
                                    </Paper>
                                </Grow>
                            )}
                        </Popper>
                    </div>
                    {/*this.props.startDate && this.setDatePicker(startDate, true)}
          {this.props.endDate && this.setDatePicker(endDate, false)}
          {this.searchButton()*/}
                </Grid>
                {filters.length > 0 && (
                    <div>Filters: {filters.map(this.filterChipsRenderer)} </div>
                )}
                {helperTextProps && (
                    <span className="disclaimer-text">{helperTextProps}</span>
                )}
            </div>
        );
    }

    // removed popperTrans helper; inlined markup for simplicity
    private readonly filterChipsRenderer = (filter: any, index: number) => {
        const { Icon, filterText, toolTip } = filter;
        const { classes } = this.props;
        return (
            <Tooltip key={index} title={toolTip}>
                <Chip
                    icon={<Icon />}
                    label={filterText}
                    onDelete={this.handleRemoveFilter(filter)}
                    className={classes.chip}
                />
            </Tooltip>
        );
    };

    private readonly searchSuggestionsRenderer = (suggestion: any, index: number) => {
        const { Icon, suffix, searchProp } = suggestion;
        const { searchText, cursor } = this.state;
        if (!this.isDuplicateFilter(searchProp)) {
            return (
                <div
                    key={searchProp}
                    id={searchProp}
                    className={
                        cursor === index ? 'selected-suggestion' : 'suggestion-item'
                    }
                >
                    <MenuItem
                        onClick={this.handleSearchSuggestionClick(suggestion)}
                    >
                        <Icon className="menu-icon" /> <span>{searchText}</span>{' '}
                        <em> {suffix}</em>
                    </MenuItem>
                </div>
            );
        }
    };
    private readonly setSearchElement = (node: any) => {
        this.searchElement = node;
    };
}

export default withStyles(styles)(SearchSection);
